import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/shared/lib/cn.ts'

export interface Notice {
  text: string
  /** Stays until closed: the moderator still has something to do (write to the owner). */
  sticky: boolean
}

const HIDE_AFTER_MS = 8000

/**
 * What the last decision did. On a phone the moderator is deep in the list, so it floats above the tab bar.
 * The live region is always mounted and only its text changes — screen readers skip regions inserted already filled.
 */
export function DecisionNotice({ notice, onClose }: { notice: Notice | null; onClose: () => void }) {
  useEffect(() => {
    if (!notice || notice.sticky) return
    const timer = setTimeout(onClose, HIDE_AFTER_MS)
    return () => clearTimeout(timer)
  }, [notice, onClose])

  return (
    <>
      <div
        role="status"
        className={cn(
          notice
            ? 'fixed inset-x-3 bottom-[calc(64px+env(safe-area-inset-bottom))] z-30 flex items-start gap-2.5 rounded-control border border-[#bfd4c5] bg-brand-tint px-3.5 py-2.5 text-[14px] text-brand-ink shadow-panel md:static md:shadow-none'
            : 'sr-only',
        )}
      >
        {notice && (
          <>
            <span className="min-w-0 flex-1 text-balance-pretty">{notice.text}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Скрыть"
              className="-m-2 flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-brand-ring"
            >
              <X className="size-4" aria-hidden />
            </button>
          </>
        )}
      </div>
      {/* Room at the end of the list so the floating notice never covers the last card. */}
      {notice && <div className="order-last h-28 md:hidden" aria-hidden />}
    </>
  )
}
